import type { VehicleColour } from '../../data/vehicles'

/* The paints a card offers, as swatches you can actually pick.

   Choosing one says which colour the visitor wants this car in, and nothing
   else. The figures on the card hold still: the price, the instalment and the
   deposit belong to the car the card is quoting, and pressing a swatch is not
   a request to be quoted a different plate number. The pick travels with the
   booking instead — it is stated on the form and filed with the order, and
   the desk confirms the paint when it calls.

   Buttons in a radio group, not links: one exclusive choice out of a few,
   which is both what the pick is and what a screen reader should hear.

   The swatch is the paint itself, so it cannot carry the chosen state in its
   own fill — a white one has no edge to thicken. The ring outside it does,
   and the name is read out beside it for anyone who cannot tell two nearly
   identical greys apart. */

type ColourSwatchesProps = {
  colours: VehicleColour[]
  /** The id of the paint the visitor has picked. */
  value: string
  onChange: (id: string) => void
  /** The car being coloured, for the group's accessible name. */
  name: string
  /** 'sm' on the inventory grid's compact card, 'md' on the editorial one. */
  size?: 'sm' | 'md'
  className?: string
}

function ColourSwatches({
  colours,
  value,
  onChange,
  name,
  size = 'sm',
  className = '',
}: ColourSwatchesProps) {
  /* One paint is a fact about the car, not a choice to offer. */
  if (colours.length < 2) return null

  return (
    <div
      role="radiogroup"
      aria-label={`Colours the ${name} is offered in`}
      className={`flex flex-wrap items-center ${size === 'md' ? 'gap-1' : 'gap-0.5'} ${className}`}
    >
      {colours.map((option) => {
        const current = option.id === value

        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={current}
            title={
              current
                ? `${option.name} — chosen`
                : `${option.name} — book it in this colour`
            }
            onClick={() => onChange(option.id)}
            className="group/swatch block cursor-pointer p-1 focus:outline-none"
          >
            {/* The ring is written one way or the other rather than layered:
                the two rules set the same properties, and which wins is the
                order Tailwind emitted them in, not the order they are
                written here. */}
            <span
              aria-hidden="true"
              className={`block rounded-full ring-offset-2 ring-offset-raised transition duration-200 group-focus-visible/swatch:ring-2 group-focus-visible/swatch:ring-lime group-hover/swatch:scale-110 ${
                size === 'md' ? 'size-4.5' : 'size-4'
              } ${current ? 'ring-2 ring-lime' : 'ring-1 ring-white/15'} ${
                option.available ? '' : 'opacity-60'
              }`}
              style={{ backgroundColor: option.hex }}
            />
            <span className="sr-only">
              {option.name}
              {option.available ? '' : ' — currently on hold'}
            </span>
          </button>
        )
      })}
    </div>
  )
}

export default ColourSwatches
